import {
  OWNER_ONLY_PERMISSIONS,
  OWNER_ROLE_KEYS,
  type IPermissionHolderRole,
  type PermissionType,
} from '@featit/shared/access-control';
import { type IRefusalDestination } from '@featit/shared/refusal-destination';
import { isGuestWorkArea } from '@featit/shared/guest-work-area';
import { type IWorkAreaAccess } from '@featit/shared/types/index';
import { action, observable } from 'mobx';
import { common } from '@featit/shared/i18n/messages/en/common';
import { getAsIfState } from '@/features/as-if/asIfView';
import type { MessageArgs } from '@featit/shared/i18n/core';
import type { MessageKey } from '@featit/shared/i18n/messages/index';
import { registerEnglishArea } from '@/i18n/catalogue';
import { englishT } from '@/i18n/LocaleProvider';
import { pageT } from '@/i18n/pageTranslator';

registerEnglishArea('common', common);

/**
 * I-1925: the one line a control says when the reader lacks `permission` (F-0324), in the page's
 * language: `common.permissionNeeded` with the permission's `common.permissionAction`, whose English
 * is `permissionNeeded`'s word for word. Every permission refusal the web builds says it through here.
 * PX-1 (§24.5, D-0432 option a): in a customer guest's work area it says `common.guestRefusal`
 * instead, so a customer never reads "permission" or "work area"; a member keeps the permission.
 */
export function permissionRefusal(
  permission: PermissionType,
  workArea?: IGatedWorkArea | null,
): string {
  return (
    asIfDeniedReason(permission) ??
    (isGuestWorkArea(workArea) ? refusalLine('common.guestRefusal') : null) ??
    refusalLine('common.permissionNeeded', { action: permissionAction(permission) })
  );
}

type AsIfCeilingAskerType = (permission: PermissionType) => string | null;
let askAsIfCeiling: AsIfCeilingAskerType | null = null;

/**
 * The as-if UI chunk (`features/as-if/asIfCeilingAsker.ts`) hands in the one function that knows the
 * ceiling and its sentences. The launch entry carries none of them: a tab with no view never asks, and the
 * boot of a tab that holds one loads the chunk before the first render (`loadAsIfUi`).
 */
export function registerAsIfCeilingAsker(asker: AsIfCeilingAskerType | null): void {
  askAsIfCeiling = asker;
}

/**
 * F-0709 ASIF-17 to ASIF-28 (`docs/concept-start-a-work-area-as-if.md` §2.3): while this tab holds an
 * as-if view, an act on the ceiling (`ASIF_CEILING_DENIED`) is done as oneself. Its control is drawn
 * disabled with this one sentence, in the page's language, never hidden; `null` when the act is not
 * on the ceiling or no view is held. Every gate below asks it first, and `useAsIfDenied` is the same
 * answer for a control that does not use a gate.
 */
export function asIfDeniedReason(permission: PermissionType): string | null {
  if (!getAsIfState().view) return null;
  // A view is held and the as-if UI chunk (which knows the ceiling) is not in yet: its fetch failed at
  // boot, or a view began a moment before the page reloads onto it. The gate fails closed, never open:
  // every control is drawn refused, with this one waiting sentence, until the chunk gives the ceiling's own.
  if (!askAsIfCeiling) return refusalLine('common.asIfLoading');
  return askAsIfCeiling(permission);
}

export function permissionAction(permission: PermissionType): string {
  return refusalLine(`common.permissionAction.${permission.replace('.', '_')}` as MessageKey);
}

/**
 * I-2193: a role's name in the reader's language, as a package gives it (`usePackageNamesOf`). A caller
 * that has the work area's package names hands it in; one that has not says the stored names, as before.
 */
export type RoleNameFn = (name: string) => string;

/**
 * I-1925: `onlyHoldersCan` (`access-control.ts`) in the page's language, its English word for word:
 * the Owner-only permission's line naming the roles that hold it here. `null` without holders.
 */
export function holdersRefusal(
  permission: PermissionType,
  holders: readonly IPermissionHolderRole[] | null | undefined,
  roleName: RoleNameFn = (name) => name,
): string | null {
  if (!holders?.length) return null;
  const action = permissionAction(permission);
  const ownerHolds = holders.some((role) => OWNER_ROLE_KEYS.includes(role.key));
  const others = [
    ...new Set(
      holders
        .filter((role) => !OWNER_ROLE_KEYS.includes(role.key))
        .map((role) => roleName(role.name)),
    ),
  ].sort((left, right) => left.localeCompare(right));
  if (!others.length) return refusalLine('common.onlyOwnerCan', { action });
  const names = [
    ...(ownerHolds ? [refusalLine('common.holderOwner')] : []),
    ...others.map((role) =>
      refusalLine(/^[aeiou]/i.test(role) ? 'common.holderRoleVowel' : 'common.holderRole', {
        role,
      }),
    ),
  ];
  const holdersText =
    names.length === 1
      ? names[0]
      : refusalLine('common.holdersList', {
          list: names.slice(0, -1).join(', '),
          last: names[names.length - 1],
        });
  return refusalLine('common.onlyHoldersCan', { holders: holdersText, action });
}

/**
 * I-1925: `namespaceCreateRefusal` (`namespaces.ts`) in the page's language, its English word for
 * word: why creating here is refused, from the roles the reader holds here (I-1679, CA-6).
 */
export function namespaceCreateRefusal(rolesHere: readonly string[] | null | undefined): string {
  if (!rolesHere) return refusalLine('common.namespaceCreate.unknown');
  if (rolesHere.length === 0) return refusalLine('common.namespaceCreate.none');
  return refusalLine(
    rolesHere.length === 1 ? 'common.namespaceCreate.oneRole' : 'common.namespaceCreate.manyRoles',
    { roles: rolesHere.join(', ') },
  );
}

/** A refusal's one line from the catalogue, in the page's language (I-1925). */
export function refusalLine(key: MessageKey, args?: MessageArgs): string {
  return pageT(englishT)(key, args);
}

/**
 * Whether this reader may use a control in one work area, and if not, why (I-0365, I-0676).
 *
 * ⭐ Asked of the **permission** that governs the control, never of a role, a membership grade or the
 * kind of credential presenting the request — decision 3 (2026-09-16): *"keep clear separation of
 * concerns between permission and role, so that future roles just need the permission assignment."*
 * A verification session is refused here because its credential's ceiling removed the permission
 * from `access.permissions` on the server, not because a screen recognised the session.
 *
 * ⛔ **Three states, not two** (I-0676, concept-access-control §14.2a). `access.permissions` is
 * absent both while the work area **has not loaded** and when a **server older than the field**
 * answers without it. Reading both as "permitted" drew every gated control as usable for the first
 * fraction of a second — the mobile `+` was an enabled link for ~140 ms on the bundle for a session
 * that may not create. So the caller hands over the **work area**, and its absence is its own answer:
 *
 * - no work area yet → `pending`: present, not actionable, announced as checking access;
 * - a work area whose summary lists permissions → `permitted` or `refused`, with the one-line
 *   reason from `permissionNeeded` (F-0324: short and neutral, no work-area name or key), or for an
 *   Owner-only permission the roles that hold it (`onlyHoldersCan`, I-1462);
 * - a work area whose summary has no list (an older server) → `permitted`, **explicitly**, with the
 *   basis `server-without-field`: the server still refuses what it must, and guessing a refusal from
 *   `canManage` would disable controls a contributor who cannot manage may use.
 *
 * ⚠️ This file is the only reader of an access summary's `permissions` in `web/src`;
 * `workAreaPermissionGate.guard.test.ts` names any other file that reads it.
 *
 * In the web rather than `@featit/shared` because every caller is a web screen today; mobile has no
 * such control yet. Move it when a second platform needs the same sentence.
 */
export type WorkAreaGateType =
  | { state: 'pending'; again?: true }
  | { state: 'permitted'; basis: 'granted' | 'server-without-field' }
  | {
      state: 'refused';
      reason: string;
      retry?: () => void;
      /**
       * I-1914: where the remedy is, and what the viewer may do there. With one the viewer may open,
       * pressing the control opens its reason with a link (`useRefusalDestination`).
       */
      destination?: IRefusalDestination;
    };

/** A work area as the gate reads it: its name for the sentence, its access summary for the answer. */
export interface IGatedWorkArea {
  name?: string | null;
  access?: Pick<IWorkAreaAccess, 'permissions' | 'holders' | 'guest'> | null;
}

/**
 * The permissions this work area's access summary lists, for the one reader that needs the list itself
 * rather than a gate (the as-if start works out what a role holds above the starter, ASIF-16);
 * `undefined` while unread. Every control asks a gate, never this.
 */
export function permissionsHeldIn(workArea: IGatedWorkArea | null | undefined) {
  return workArea?.access?.permissions;
}

/**
 * PX-18 (D-0427 a, D-0432 b): whether a team control is drawn at all. A customer guest's frame reads
 * their grants, never their role's name: a control whose permission the guest holds here is drawn,
 * and works, as for a member; one they do not hold is absent, not drawn disabled. Every member is
 * answered `true`, so the control is drawn and its gate disables it with the reason. A permission the
 * guest holds and the as-if ceiling denies stays drawn, refused with the ceiling's reason.
 *
 * F-0758 (with F-0709): viewing as Customer, the listing's permissions are what the view may use, the
 * ceiling and the starter's own rights already taken away (`asIfEffectivePermissions`), so the role's
 * own control would read as not held and vanish. The view's `limited` names what the role holds and
 * the view may not use: such a control is still the customer's, drawn, and its gate refuses it with
 * the ceiling's sentence (`asIfDeniedReason`).
 */
export function guestFrameOffers(
  workArea: IGatedWorkArea | null | undefined,
  permission: PermissionType,
): boolean {
  if (!isGuestWorkArea(workArea)) return true;
  if (workArea?.access?.permissions?.includes(permission)) return true;
  return getAsIfState().view?.limited?.includes(permission) ?? false;
}

/** What a pending control says — to a screen reader in its name, and on hover in its title. */
export const CHECKING_ACCESS = 'checking access';

/**
 * A namespace's own create controls (New subgroup, New work area): `work_area.manage` there, as
 * `namespaceTree`'s `canManageHere` answers it (I-1054, §10.13), refused with the one sentence
 * `namespaceCreateRefusal` builds from the roles the reader holds there (I-1679, CA-6) — shared by
 * `Layout.tsx`'s sidebar and `OrganisationWorkAreasSettings`'s own buttons, so it reads the same
 * wherever the control appears. No namespace yet is pending — until `read.treeLoaded` says the
 * tree has answered without it, which refuses with `NAMESPACE_NOT_IN_TREE_REASON` (MWI-24, I-1768:
 * KD-17's bound; an address naming no namespace of the reader's left the controls checking for good).
 */
export const NAMESPACE_NOT_IN_TREE_REASON = common.namespaceNotInTree;

export function namespaceCreateGate(
  namespace:
    | { canManageHere?: boolean | null; rolesHere?: readonly string[] | null }
    | null
    | undefined,
  read?: { treeLoaded: boolean },
): WorkAreaGateType {
  if (!namespace && read?.treeLoaded) {
    return { state: 'refused', reason: refusalLine('common.namespaceNotInTree') };
  }
  // A namespace that is here has answered: a missing `canManageHere` on it is unread (KD-17).
  return heldGate(
    namespace?.canManageHere,
    { reason: namespaceCreateRefusal(namespace?.rolesHere) },
    { settled: Boolean(namespace) },
  );
}

/**
 * The work-area list's own read — `ProjectStore`'s discovery — as every `workAreaGate` waits on it
 * (I-1702, KD-17). `ProjectStore` reports it here, so the 40-odd callers that hand over a work area
 * still to arrive are bounded in one place rather than each: while discovery has failed (or timed
 * out) a work area that is not here is unread, refused with Retry, not pending for good.
 */
const workAreaDiscovery = observable(
  { failed: false, retrying: false, retry: undefined as (() => void) | undefined },
  { retry: false },
);

/** `retrying` (I-1708, KD-17a): a discovery re-read is in flight after the last one failed. */
export const reportWorkAreaDiscovery = action(
  (state: { failed: boolean; retrying?: boolean; retry?: () => void }): void => {
    workAreaDiscovery.failed = state.failed;
    workAreaDiscovery.retrying = Boolean(state.retrying);
    workAreaDiscovery.retry = state.retry;
  },
);

/** Whether the last discovery failed, so a re-read starting now is a retry (I-1708, KD-17a). */
export const workAreaDiscoveryFailed = (): boolean => workAreaDiscovery.failed;

export function workAreaGate(
  workArea: IGatedWorkArea | null | undefined,
  permission: PermissionType,
  roleName: RoleNameFn = (name) => name,
): WorkAreaGateType {
  const denied = asIfDeniedReason(permission);
  if (denied) return { state: 'refused', reason: denied };
  if (!workArea) {
    return accessReadGate({
      settled: workAreaDiscovery.failed,
      retrying: workAreaDiscovery.retrying,
      retry: workAreaDiscovery.retry,
    });
  }
  const held = workArea.access?.permissions;
  if (!held) return { state: 'permitted', basis: 'server-without-field' };
  if (held.includes(permission)) return { state: 'permitted', basis: 'granted' };
  return { state: 'refused', reason: refusalReason(workArea, permission, roleName) };
}

/**
 * The one line a refusal shows (F-0324). For an Owner-only permission it names who can (I-1462):
 * the roles the server says hold it here — *Only the Owner or a Release manager can let agents pause
 * sources*, or the Owner's alone. A screen never names the authority itself, so a custom role given
 * the permission is named on every control it governs. Without the server's answer, the permission.
 */
function refusalReason(
  workArea: IGatedWorkArea,
  permission: PermissionType,
  roleName: RoleNameFn,
): string {
  // A guest is told no holders (`GUEST_WORK_AREA_ACCESS_FIELDS`) and reads the guest's line.
  if (OWNER_ONLY_PERMISSIONS.includes(permission) && !isGuestWorkArea(workArea)) {
    const named = holdersRefusal(permission, workArea.access?.holders?.[permission], roleName);
    if (named) return named;
  }
  return permissionRefusal(permission, workArea);
}

/**
 * The read a `heldGate` answer comes from (I-1702, KD-17 in concept-web-keyboard-accessibility.md):
 * whether it has **settled** — answered, or failed — and how to ask again. Without it a failed read
 * and a malformed answer both look like "not arrived yet", and the control waited for good.
 */
export interface IAccessRead {
  settled: boolean;
  /**
   * The read is being asked again after it settled unread (I-1708, KD-17a): the control is pending,
   * checking again, until the retry answers — not still refused with the failure it is retrying.
   */
  retrying?: boolean;
  retry?: () => void;
  /** The line an unread refusal shows, where a screen has a better one than `ACCESS_UNREAD_REASON`. */
  unreadReason?: string;
}

/**
 * The read of an answer whose own record is already here — a loaded listing's `canManage`, a
 * document's `canEdit`: it has settled, so a field missing from it is unread, not pending.
 */
export const ANSWERED_READ: IAccessRead = { settled: true };

// The unread texts live in a module that imports only the English catalogue's data, so a Playwright
// spec can name them without loading this module's i18n (whose build-time constants only Vite defines).
import {
  ACCESS_UNREAD_NO_RETRY_REASON,
  ACCESS_UNREAD_REASON,
  CHECKING_ACCESS_AGAIN,
} from './accessUnreadText';

export { ACCESS_UNREAD_NO_RETRY_REASON, ACCESS_UNREAD_REASON, CHECKING_ACCESS_AGAIN };

/**
 * The gate of a read that has not answered yes or no (I-1702, KD-17): pending while it is in flight,
 * then — failed, timed out (`settleWithin`), or malformed — refused with the unread reason and the
 * read's `retry`, and pending again, checking again, while that retry runs (I-1708, KD-17a). Every
 * gate built from a read goes through here; none builds `{ state: 'pending' }` itself
 * (`accessReadBounded.guard.test.ts`).
 */
export function accessReadGate(read: IAccessRead | undefined): WorkAreaGateType {
  if (!read?.settled) return { state: 'pending' };
  if (read.retrying) return { state: 'pending', again: true };
  return {
    state: 'refused',
    reason:
      read.unreadReason ??
      refusalLine(read.retry ? 'common.accessUnread' : 'common.accessUnreadNoRetry'),
    ...(read.retry ? { retry: read.retry } : {}),
  };
}

/**
 * The same three states for a control whose answer the server already gave as a boolean — a
 * listing's `canManage`, a document's `canEdit` (F-0324). `reason` is the permission the server
 * asked, or, for an authority that is not a permission (the work area's owner, the person
 * themselves), the one line to show.
 *
 * ⛔ **Pending is bounded by the read** (I-1702, KD-17). Only `true` permits and only `false`
 * refuses. Anything else is pending while `read` has not settled, and once it has settled — the
 * read failed, or answered without a boolean — refused with `ACCESS_UNREAD_REASON` and the read's
 * `retry` (without one, `ACCESS_UNREAD_NO_RETRY_REASON`), which `gateControlProps` runs when the control is pressed. `accessReadBounded.guard.test.ts`
 * names a caller whose answer is not a literal and that passes no read.
 */
export function heldGate(
  held: boolean | null | undefined,
  reason: PermissionType | { reason: string },
  read?: IAccessRead,
  /** PX-1: the work area the control acts in, so a customer guest is refused in their words. */
  workArea?: IGatedWorkArea | null,
): WorkAreaGateType {
  const denied = typeof reason === 'string' ? asIfDeniedReason(reason) : null;
  if (denied) return { state: 'refused', reason: denied };
  if (held === true) return { state: 'permitted', basis: 'granted' };
  if (held === false) {
    return {
      state: 'refused',
      reason: typeof reason === 'string' ? permissionRefusal(reason, workArea) : reason.reason,
    };
  }
  return accessReadGate(read);
}

/**
 * The same rule over the work areas one control acts across — a view's member action (I-0365): refused
 * when any of them refuses, naming that one; pending while any has not loaded; permitted otherwise.
 * `ids` absent from `workAreas` count as not loaded yet.
 */
export function workAreasGate(
  ids: readonly string[],
  workAreas: ReadonlyArray<IGatedWorkArea & { id: string }>,
  permission: PermissionType,
): WorkAreaGateType {
  const gates = ids.map((id): WorkAreaGateType => {
    const workArea = workAreas.find((candidate) => candidate.id === id);
    const gate = workAreaGate(workArea, permission);
    // Across several work areas the line says which one refused; one work area needs no name.
    return gate.state === 'refused' && ids.length > 1 && workArea?.name
      ? { state: 'refused', reason: `${gate.reason} in ${workArea.name}` }
      : gate;
  });
  return (
    gates.find((gate) => gate.state === 'refused') ??
    gates.find((gate) => gate.state === 'pending') ??
    gates[0] ?? { state: 'pending' }
  );
}

/**
 * One control that asks several permissions of the server at once — a decision's Supersede asks
 * `work_item.update` on the old decision and `work_item.create` for the new one (F-0324): refused
 * when any refuses, naming the first that does; pending while any is; permitted otherwise.
 */
export function everyGate(...gates: readonly WorkAreaGateType[]): WorkAreaGateType {
  return (
    gates.find((gate) => gate.state === 'refused') ??
    gates.find((gate) => gate.state === 'pending') ??
    gates[0] ?? { state: 'pending' }
  );
}

/** Whether the control must not act: while pending as much as when refused. */
export function gateBlocks(gate: WorkAreaGateType): boolean {
  return gate.state !== 'permitted';
}

/**
 * Whether ANY of these work areas grants `permission` — the three-state rule (I-0676) applied
 * across a list, the way `workAreasGate` applies it to one control's own several work areas.
 *
 * I-1039 (`useShellReachability.ts`): the toolbar's reachability check asks this once per
 * permission it cares about, rather than reading `access.permissions` itself. A work area that has
 * not loaded its own access summary, or whose server predates the field, reads as **permitted**
 * here — `workAreaGate`'s `server-without-field` basis — exactly as it would for one control; it is
 * never read as "holds nothing", which is the raw-read defect this file exists to rule out
 * (`workAreaPermissionGate.guard.test.ts`).
 */
export function permissionReachableAnywhere(
  workAreas: ReadonlyArray<IGatedWorkArea>,
  permission: PermissionType,
): boolean {
  return workAreas.some((workArea) => workAreaGate(workArea, permission).state === 'permitted');
}

/**
 * The sentence a blocked control shows in its `title`: the refusal, or that access is still being
 * checked. `null` when the control is usable.
 */
export function gateReason(gate: WorkAreaGateType): string | null {
  if (gate.state === 'refused') return gate.reason;
  if (gate.state === 'pending')
    return refusalLine(gate.again ? 'common.checkingAccessAgain' : 'common.checkingAccess');
  return null;
}
